import { BarChart, LineChart } from "echarts/charts";
import { GridComponent, TitleComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";
import { BREAKPOINT } from "@/configs/breakpoints";
import { useDevicePixelRatio } from "@/hooks/use-device-pixel-ratio";
import { useSupportedLocale } from "@/hooks/use-locale";
import { useMediaQuery } from "@/hooks/use-media-query";
import { AxisSide, type TimeAxisPanelSpec } from "../ChartSpec";
import { useHoverTime } from "../crosshair/use-hover-time";
import { useCrosshairBus, useDataViewerStore, useWidthGroupRegistry } from "../data-viewer-provider";
import type { StreamResolutions } from "../resolution/panel-resolution-slice";
import { pickStreamResolution } from "../resolution/pick-resolution";
import type { RequestedStream } from "../stream-data/types";
import { useEnsureStreamData } from "../stream-data/use-ensure-stream-data";
import { useStreamData } from "../stream-data/use-stream-data";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import { buildTimePanelOptions } from "./build-time-panel-options";
import { HoverValues } from "./hover-values/hover-values";
import { PlotChromeLayer } from "./layout/plot-chrome-layer";
import { X_AXIS_BAND_PX } from "./layout/plot-geometry";
import { usePanelPlotLayout } from "./layout/use-panel-plot-layout";
import { PanelLegend } from "./panel-legend";
import { PanelMetricBadge } from "./panel-metric-badge";
import { planPanelChrome } from "./plan-panel-chrome";
import { type GetConversion, prepareSeries } from "./prepare-series";
import { PANEL_WIDTH_GRIP_RESERVE_PX, ResizerGrip } from "./resize/resizer-grip";
import { usePanelResize } from "./resize/use-panel-resize";
import { UnitBadge } from "./unit-badge/unit-badge";
import { useLeftYAxisSelection } from "./use-left-y-axis-selection";

/** Plot box heights from the spec (§6), which the x-axis label band sits below. */
const DEFAULT_PLOT_HEIGHT_PX = 150;
const MOBILE_PLOT_HEIGHT_PX = 128;
const DEFAULT_PANEL_ROW_GAP_PX = 4;

/** Chart card box (§4). The plot gets the panel width, so the card adds its border and padding on top. */
const CARD_BORDER_PX = 1;
const CARD_PADDING_PX = 12;
const PLOT_INSET_IN_CARD_PX = CARD_BORDER_PX + CARD_PADDING_PX;

echarts.use([LineChart, BarChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);

// Skip re-render when another panel's spec changes — this panel's props are unchanged.
export const Panel = memo(
    ({
        spec,
        groupId,
        maxStackWidth,
        syncCrosshair = true,
        emitsHoverTime = true,
        resizable = true,
        baseHeightPx,
    }: {
        spec: TimeAxisPanelSpec;
        /** Width group this panel shares gutters with — all panels in a stack pass the same id. */
        groupId: string;
        maxStackWidth: number;
        syncCrosshair?: boolean;
        emitsHoverTime?: boolean;
        resizable?: boolean;
        baseHeightPx?: number;
    }) => {
        const isMobile = useMediaQuery(BREAKPOINT.SM);
        const defaultHeight =
            baseHeightPx ?? (isMobile ? MOBILE_PLOT_HEIGHT_PX : DEFAULT_PLOT_HEIGHT_PX) + X_AXIS_BAND_PX;

        const chartRef = useRef<HTMLDivElement | null>(null);
        const chartInstanceRef = useRef<echarts.ECharts | null>(null);
        const crosshairBus = useCrosshairBus();
        const hoverTime = useHoverTime();
        const devicePixelRatio = useDevicePixelRatio();

        const startMs = useDataViewerStore((state) => state.startMs);
        const endMs = useDataViewerStore((state) => state.endMs);

        const gripReservePx = resizable ? PANEL_WIDTH_GRIP_RESERVE_PX + DEFAULT_PANEL_ROW_GAP_PX : 0;
        const maxWidth = Math.max(0, maxStackWidth - gripReservePx - PLOT_INSET_IN_CARD_PX * 2);
        const { size, heightHandleProps, widthHandleProps } = usePanelResize({
            maxWidth,
            defaultHeight,
        });
        const forceRaw = useDataViewerStore((state) => state.debugForceRawStreams);

        // Strip display fields — fetch hooks only need streamId, its resolution and how to aggregate its buckets.
        const requestedStreams = useMemo<RequestedStream[]>(
            () =>
                spec.streams.map((stream) => ({
                    streamId: stream.streamId,
                    resolution: forceRaw ? "raw" : pickStreamResolution(stream.seriesType, size.width, startMs, endMs),
                    aggregate: stream.aggregate,
                })),
            [spec.streams, forceRaw, size.width, startMs, endMs],
        );

        const setPanelResolutions = useDataViewerStore((state) => state.setPanelResolutions);
        const clearPanelResolutions = useDataViewerStore((state) => state.clearPanelResolutions);
        const resolutionsKey = requestedStreams.map((stream) => `${stream.streamId}:${stream.resolution}`).join(",");
        useEffect(() => {
            const resolutions: StreamResolutions = Object.fromEntries(
                requestedStreams.map((stream) => [stream.streamId, stream.resolution]),
            );
            setPanelResolutions(groupId, spec.id, resolutions);
            return () => clearPanelResolutions(groupId, spec.id);
            // eslint-disable-next-line react-hooks/exhaustive-deps -- resolutionsKey stabilizes requestedStreams, which changes on every pan
        }, [groupId, spec.id, resolutionsKey, setPanelResolutions, clearPanelResolutions]);

        const getDisplayInfo = useGetDisplayInfo();
        const locale = useSupportedLocale();
        const { leftAxes, activeLeftAxisId, setActiveLeftAxisId } = useLeftYAxisSelection(spec.id, spec.axes);

        // Kick off a fetch for any missing data; read windowed points from cache. See stream-data/README.md.
        const isFetching = useEnsureStreamData({ streams: requestedStreams, startMs, endMs });
        const data = useStreamData({ streams: requestedStreams, startMs, endMs });

        // Spinner only while fetching AND at least one stream has no data yet.
        // Panning into cached range shows data immediately; multi-stream waits for all.
        const isLoadingStreamData = isFetching && requestedStreams.some((stream) => !(stream.streamId in data));

        // Join cached points with spec (color, name, axis) and convert to display units.
        const preparedSeries = useMemo(() => {
            const getConversion: GetConversion = (unitCategory) => getDisplayInfo(unitCategory).fromDatabase;
            return prepareSeries(data, spec.streams, spec.axes, getConversion);
        }, [data, spec.streams, spec.axes, getDisplayInfo]);

        // One layout drives both the echarts grid and the DOM chrome layer, so they can't drift apart.
        const registry = useWidthGroupRegistry();
        const { layout, axisScales } = usePanelPlotLayout({
            axes: spec.axes,
            streams: spec.streams,
            preparedSeries,
            activeLeftAxisId,
            panelHeightPx: size.height,
            groupId,
            memberId: spec.id,
            registry,
        });

        const plotBottomPx = Math.max(size.height - layout.xAxisBandPx, 0);

        const chrome = useMemo(
            () => planPanelChrome({ axes: spec.axes, series: preparedSeries, plotBottomPx }),
            [spec.axes, preparedSeries, plotBottomPx],
        );

        const chartOptions = useMemo(
            () =>
                buildTimePanelOptions(
                    spec,
                    preparedSeries,
                    layout,
                    axisScales,
                    { startMs, endMs },
                    activeLeftAxisId,
                    getDisplayInfo,
                    locale,
                    chrome,
                ),
            [
                spec,
                preparedSeries,
                layout,
                axisScales,
                startMs,
                endMs,
                activeLeftAxisId,
                getDisplayInfo,
                locale,
                chrome,
            ],
        );

        // Ref so the init effect reads latest options without re-running.
        const chartOptionsRef = useRef(chartOptions);
        chartOptionsRef.current = chartOptions;

        // Init chart once on mount. Option updates go through setOption below.
        useLayoutEffect(() => {
            const chartContainer = chartRef.current;
            if (!chartContainer) return undefined;

            const existing = echarts.getInstanceByDom(chartContainer);
            if (existing) {
                crosshairBus.unregister(existing);
                existing.dispose();
            }

            // ECharts freezes the pixel ratio at init, so a change of screen re-runs this effect.
            const instance = echarts.init(chartContainer, undefined, { devicePixelRatio });
            instance.setOption(chartOptionsRef.current);

            if (syncCrosshair) crosshairBus.register(instance);
            if (emitsHoverTime) hoverTime.enable(instance);
            chartInstanceRef.current = instance;

            return () => {
                chartInstanceRef.current = null;
                hoverTime.disable();
                crosshairBus.unregister(instance);
                instance.dispose();
            };
        }, [syncCrosshair, emitsHoverTime, crosshairBus, hoverTime, devicePixelRatio]);

        useEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.resize();
            }
        }, [size.height, size.width]);

        // Push option changes without tearing down the chart instance.
        // replaceMerge: shrinking series or y-axis lists must replace those arrays — default merge leaves orphans.
        //
        // useLayoutEffect so ECharts picks up gutter changes before the browser paints. useEffect would
        // flash the old (too narrow) grid for one frame when a sibling panel mounts and widens the stack.
        useLayoutEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.setOption(chartOptions, {
                    replaceMerge: ["series", "yAxis"],
                });
            }
        }, [chartOptions]);

        const cardWidthPx = size.width + PLOT_INSET_IN_CARD_PX * 2;

        const noDataForRange =
            requestedStreams.length > 0 && preparedSeries.every((series) => series.points.length === 0);

        return (
            <div className={"w-full"}>
                <div className={"inline-flex max-w-full"}>
                    {/* items-start: centering this column would shift the height grip off the card's center. */}
                    <div className={"flex flex-col items-start gap-[2px]"}>
                        <div className={"flex shrink-0 items-center gap-[4px]"}>
                            <div
                                className={"flex shrink-0 flex-col gap-[12px] rounded-[10px] border bg-chart-card"}
                                style={{
                                    width: cardWidthPx,
                                    borderWidth: CARD_BORDER_PX,
                                    padding: CARD_PADDING_PX,
                                }}
                            >
                                {/* Each unit picker sits above its own axis, so with two axes they go to opposite ends. */}
                                <div className={"flex items-start gap-[8px]"}>
                                    <UnitBadge
                                        side={AxisSide.Left}
                                        axes={spec.axes}
                                        getDisplayInfo={getDisplayInfo}
                                        activeLeftAxisId={activeLeftAxisId}
                                        leftAxes={leftAxes}
                                        onActiveLeftAxisChange={setActiveLeftAxisId}
                                    />
                                    <PanelMetricBadge
                                        unitCategory={chrome.metricCategory}
                                        deviceName={chrome.deviceName}
                                    />
                                    <div className={"ml-auto flex shrink-0 items-center"}>
                                        <UnitBadge
                                            side={AxisSide.Right}
                                            axes={spec.axes}
                                            getDisplayInfo={getDisplayInfo}
                                            activeLeftAxisId={activeLeftAxisId}
                                            leftAxes={leftAxes}
                                            onActiveLeftAxisChange={setActiveLeftAxisId}
                                        />
                                    </div>
                                </div>
                                <div className={"relative shrink-0"} style={{ height: size.height }}>
                                    <div ref={chartRef} className={"size-full"} />

                                    {/* Chrome layer's box IS the plot rect: it carries the crisp 1px grid border. */}
                                    <PlotChromeLayer layout={layout} />

                                    <HoverValues
                                        chartRef={chartInstanceRef}
                                        series={preparedSeries}
                                        plotBottomPx={plotBottomPx}
                                        panelWidthPx={size.width}
                                        hoverTags={chrome.hoverTags}
                                    />

                                    {!isLoadingStreamData && noDataForRange && (
                                        <div
                                            className={
                                                "absolute inset-0 flex items-center justify-center bg-background/60"
                                            }
                                        >
                                            <div className={"text-sm text-muted-foreground"}>No data for range</div>
                                        </div>
                                    )}
                                    {isLoadingStreamData ? (
                                        <div
                                            className={
                                                "absolute inset-0 flex items-center justify-center bg-background/60"
                                            }
                                        >
                                            <Spinner className={"size-6 text-muted-foreground"} />
                                        </div>
                                    ) : null}
                                </div>
                                <PanelLegend series={preparedSeries} parts={chrome.legend} />
                            </div>
                            {resizable && <ResizerGrip orientation={"vertical"} handleProps={widthHandleProps} />}
                        </div>
                        {resizable && (
                            <div className={"flex justify-center"} style={{ width: cardWidthPx }}>
                                <ResizerGrip orientation={"horizontal"} handleProps={heightHandleProps} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
Panel.displayName = "Panel";
